/** PlusMenu — + button dropdown: new chat, attach, metadata, SIM, chat ID, mobile feedback. */

import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Plus,
  MessageSquare,
  Paperclip,
  MessageSquareText,
  Sparkles,
  Bookmark,
  Heart,
  HeartOff,
} from "lucide-react";
import { copyToClipboard } from "../utils/clipboard";

const PlusMenu = ({
  // Feature flags
  allowFiles,
  allowMetadata,
  matrixModeAvailable,
  activeMatrix,
  // State
  chatId,
  retrievalId,
  activatingMatrix,
  disabled,
  // Handlers
  onStartNewChat,
  onAttachmentsOpen,
  onMetadataOpen,
  onMatrixOpen,
  // Feedback (mobile only)
  onFeedbackLike,
  onFeedbackDislike,
  feedbackDisabled,
  isEmbedded,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [idCopied, setIdCopied] = useState(false);
  const containerRef = useRef(null);

  // Close on outside click or Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleCopyId = async () => {
    if (!chatId) return;
    const ok = await copyToClipboard(chatId);
    if (ok) {
      setIdCopied(true);
      setTimeout(() => {
        setIdCopied(false);
        setIsOpen(false);
      }, 1000);
    }
  };

  const handleAction = (action) => {
    action?.();
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        className={`w-[30px] h-[30px] rounded-lg flex items-center justify-center transition-all cursor-pointer disabled:opacity-35 disabled:cursor-default border-none ${
          isOpen
            ? "bg-gray-200 text-gray-900"
            : "bg-transparent text-gray-400 hover:bg-gray-100 hover:text-gray-900"
        }`}
        onClick={() => setIsOpen((prev) => !prev)}
        disabled={disabled}
        aria-label="More actions"
        aria-expanded={isOpen}
      >
        <Plus size={16} />
      </button>

      {/* Menu — always rendered, toggled via opacity for E2E innerText access */}
      <div
        className={`absolute bottom-full left-0 mb-2 bg-white border border-gray-300 rounded-xl min-w-[230px] z-50 transition-all duration-150 ${
          isOpen
            ? "opacity-100 pointer-events-auto translate-y-0"
            : "opacity-0 pointer-events-none translate-y-1"
        }`}
        style={{ padding: "4px" }}
        role="menu"
      >
        {/* New chat */}
        <button
          type="button"
          className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-lg hover:bg-gray-100 transition-colors border-none bg-transparent cursor-pointer text-left font-sans disabled:opacity-40"
          onClick={() => handleAction(onStartNewChat)}
          disabled={!retrievalId || !chatId}
          role="menuitem"
        >
          <MessageSquare size={16} className="text-gray-500 shrink-0" />
          <span>{t("newChat")}</span>
        </button>

        {/* Attach files */}
        {allowFiles && (
          <button
            type="button"
            className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-lg hover:bg-gray-100 transition-colors border-none bg-transparent cursor-pointer text-left font-sans disabled:opacity-40"
            onClick={() => handleAction(onAttachmentsOpen)}
            disabled={!retrievalId}
            role="menuitem"
          >
            <Paperclip size={16} className="text-gray-500 shrink-0" />
            <span>{t("attachFile") || "Attach files"}</span>
          </button>
        )}

        {/* Metadata */}
        {allowMetadata && (
          <button
            type="button"
            className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-lg hover:bg-gray-100 transition-colors border-none bg-transparent cursor-pointer text-left font-sans disabled:opacity-40"
            onClick={() => handleAction(onMetadataOpen)}
            disabled={!retrievalId}
            role="menuitem"
          >
            <MessageSquareText size={16} className="text-gray-500 shrink-0" />
            <span>{t("Metadata") || "Metadata"}</span>
          </button>
        )}

        {/* Self-improvement mode */}
        {matrixModeAvailable && !activeMatrix && (
          <button
            type="button"
            className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-lg hover:bg-gray-100 transition-colors border-none bg-transparent cursor-pointer text-left font-sans disabled:opacity-40"
            onClick={() => handleAction(onMatrixOpen)}
            disabled={!retrievalId || !chatId || activatingMatrix}
            role="menuitem"
          >
            <Sparkles size={16} className="text-gray-500 shrink-0" />
            <span>{t("selfImprovementMode") || "Self-improvement mode"}</span>
          </button>
        )}

        {/* Feedback — mobile only, hidden in embed */}
        {!isEmbedded && (
          <>
            <button
              type="button"
              className="w-full items-center gap-2.5 px-3 py-2 text-[13px] rounded-lg hover:bg-gray-100 transition-colors border-none bg-transparent cursor-pointer text-left font-sans disabled:opacity-40 hidden max-sm:flex"
              onClick={() => handleAction(onFeedbackLike)}
              disabled={feedbackDisabled}
              role="menuitem"
            >
              <Heart size={16} className="text-gray-500 shrink-0" />
              <span>{t("helpful")}</span>
            </button>
            <button
              type="button"
              className="w-full items-center gap-2.5 px-3 py-2 text-[13px] rounded-lg hover:bg-gray-100 transition-colors border-none bg-transparent cursor-pointer text-left font-sans disabled:opacity-40 hidden max-sm:flex"
              onClick={() => handleAction(onFeedbackDislike)}
              disabled={feedbackDisabled}
              role="menuitem"
            >
              <HeartOff size={16} className="text-gray-500 shrink-0" />
              <span>{t("notHelpful")}</span>
            </button>
            <div className="h-px bg-gray-200 mx-2 my-1 hidden max-sm:block" />
          </>
        )}

        {/* Chat ID */}
        {chatId && (
          <>
          <div className="h-px bg-gray-200 mx-2 my-1" />
          <div className="px-3 py-2 text-[12px] text-gray-700 flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wide text-gray-400">
              {t("chatIdLabel")}
            </span>
            <span className="font-mono text-[12px] text-gray-800 break-all">
              {chatId}
            </span>
          </div>
          <button
            type="button"
            className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-lg hover:bg-gray-100 transition-colors border-none bg-transparent cursor-pointer text-left font-sans"
            onClick={handleCopyId}
            role="menuitem"
          >
            <Bookmark size={16} className="text-gray-500 shrink-0" />
            <span className="flex-1">{t("copyId")}</span>
            <span className="text-[11px] text-gray-400">
              {idCopied ? t("copied") : t("copy")}
            </span>
          </button>
          </>
        )}
      </div>
    </div>
  );
};

export { PlusMenu };
